import { z } from 'zod';
import { RoleKey } from './me.js';
import { SpokenSession } from './feedback-voice.js';
import { PageInfo, pageFields } from './paging.js';

// APP-104: in-app feedback. The contracts package depends only on zod, so the value lists and
// limits here mirror packages/domain/src/feedback; apps/server/src/engines/feedback-contracts.test.ts
// fails when they drift.

export const FEEDBACK_KIND_VALUES = ['problem', 'idea', 'observation'] as const;
export const FeedbackKind = z.enum(FEEDBACK_KIND_VALUES);
export type FeedbackKind = z.infer<typeof FeedbackKind>;

export const TriageStatus = z.enum(['new', 'in_review', 'planned', 'done', 'wont_do', 'duplicate']);
export type TriageStatus = z.infer<typeof TriageStatus>;

/** What the sender sees of the status (APP-138); mirrors the domain's SENDER_STATUSES. */
export const SENDER_STATUS_VALUES = [
  'received',
  'in_review',
  'planned',
  'done',
  'not_planned',
] as const;
export const SenderStatus = z.enum(SENDER_STATUS_VALUES);
export type SenderStatus = z.infer<typeof SenderStatus>;

/** Where a conversation message came from: MyRewards, a reply email, or a Teams card or chat. */
export const FeedbackChannel = z.enum(['app', 'email', 'teams']);
export type FeedbackChannel = z.infer<typeof FeedbackChannel>;

/** A reviewer's priority; the database allows only these. */
export const FeedbackPriority = z.enum(['low', 'medium', 'high']);
export type FeedbackPriority = z.infer<typeof FeedbackPriority>;

export const ScreenshotStateDto = z.enum([
  'pending_upload',
  'attached',
  'excluded',
  'unavailable',
  'omitted',
]);
export type ScreenshotStateDto = z.infer<typeof ScreenshotStateDto>;

export const CaptureReason = z.enum(['too_large', 'unreadable', 'too_many_pixels', 'failed']);
export const OmittedReason = z.enum(['upload_failed', 'upload_refused', 'expired']);

/** The caps the schemas below use (APP-104 §4); equal to the domain's FEEDBACK_LIMITS. */
export const FEEDBACK_LIMIT_VALUES = {
  wordsChars: 16_000,
  diagnosticsBytes: 65_536,
  ringRequests: 50,
  ringErrors: 20,
  ringPages: 20,
  errorMessageChars: 300,
  stackFrames: 10,
  noteChars: 4_000,
  titleChars: 80,
  /** APP-138: the sender's reply (every channel) and the team's. */
  replyChars: 4_000,
  teamReplyChars: 8_000,
} as const;
const L = FEEDBACK_LIMIT_VALUES;

/** A route path as recorded: no query string and no fragment, ever. */
// One site-relative path: no `//` anywhere, no `.` or `..` segment (`/.//host` and `/a/..//host`
// resolve to `//host`), no whitespace, control characters or backslashes.
export const SAFE_RECORDED_PATH =
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused
  /^(?!.*\/\/)(?!(?:.*\/)?\.{1,2}(?:\/|$))\/[^\s\\?#\u0000-\u001f\u007f]*$/;

const SafePath = z
  .string()
  .min(1)
  .max(300)
  .refine((p) => !/[?#]/.test(p), 'path_has_query_or_fragment')
  // One site-relative path: never `//host` or `/\host`, and no whitespace, control characters or
  // backslashes, which browsers drop or turn into slashes (`/<tab>/host` is `//host`). A reviewer
  // follows this path, so it must stay on this site.
  .refine((p) => SAFE_RECORDED_PATH.test(p), 'path_not_site_relative');
const Stamp = z.iso.datetime({ offset: true });
const Short = (max = 200) => z.string().max(max);
const RequestId = z.string().max(100).nullable();

// ---- Captured context (§4) ----

export const FeedbackPage = z
  .object({
    path: SafePath,
    /** The matched route pattern, e.g. `/awards/:displayId`. */
    routeId: Short(300).nullable(),
    title: Short(200),
    area: Short(100),
    tab: Short(100).nullable(),
    hero: z.enum(['full', 'slim']),
  })
  .strict();
export type FeedbackPage = z.infer<typeof FeedbackPage>;

export const FeedbackClient = z
  .object({
    userAgent: Short(500),
    platform: Short(100),
    language: Short(35),
    timeZone: Short(64),
    viewport: z
      .object({
        width: z.number().int().min(0).max(100_000),
        height: z.number().int().min(0).max(100_000),
        pixelRatio: z.number().min(0).max(20),
      })
      .strict(),
    screen: z
      .object({
        width: z.number().int().min(0).max(100_000),
        height: z.number().int().min(0).max(100_000),
      })
      .strict(),
    online: z.boolean(),
    colorScheme: z.enum(['light', 'dark', 'unknown']),
    reducedMotion: z.boolean(),
    /** The web bundle's version; the server flags a tab left open over a release. */
    appVersion: Short(64),
  })
  .strict();
export type FeedbackClient = z.infer<typeof FeedbackClient>;

export const RecordedRequest = z
  .object({
    at: Stamp,
    method: z.string().min(1).max(10),
    path: SafePath,
    routeId: Short(300).nullable(),
    status: z.number().int().min(0).max(599),
    durationMs: z.number().int().min(0).max(3_600_000),
    requestId: RequestId,
  })
  .strict();
export type RecordedRequest = z.infer<typeof RecordedRequest>;

export const RecordedError = z
  .object({
    at: Stamp,
    type: z.enum([
      'window_error',
      'unhandled_rejection',
      'react_error',
      'route_error',
      'api_error',
    ]),
    area: Short(100),
    name: Short(100),
    message: z.string().max(L.errorMessageChars),
    frames: z.array(Short(300)).max(L.stackFrames),
    requestId: RequestId,
  })
  .strict();
export type RecordedError = z.infer<typeof RecordedError>;

export const VisitedPage = z
  .object({ at: Stamp, path: SafePath, routeId: Short(300).nullable() })
  .strict();
export type VisitedPage = z.infer<typeof VisitedPage>;

export const FeedbackDiagnostics = z
  .object({
    requests: z.array(RecordedRequest).max(L.ringRequests),
    errors: z.array(RecordedError).max(L.ringErrors),
    pages: z.array(VisitedPage).max(L.ringPages),
  })
  .strict();
export type FeedbackDiagnostics = z.infer<typeof FeedbackDiagnostics>;

/** The JSON's UTF-8 length against the 64 KB cap (the browser drops the oldest entries to fit). */
export function diagnosticsWithinLimit(d: FeedbackDiagnostics): boolean {
  return new TextEncoder().encode(JSON.stringify(d)).length <= L.diagnosticsBytes;
}

// ---- Reporting ----

/**
 * The part of the picture the person had on screen, in picture pixels (QA-059): the picture is the
 * whole page, with this window outlined in it.
 */
export const ScreenshotWindow = z
  .object({
    x: z.number().int().min(0).max(100_000),
    y: z.number().int().min(0).max(100_000),
    width: z.number().int().positive().max(100_000),
    height: z.number().int().positive().max(100_000),
  })
  .strict();
export type ScreenshotWindow = z.infer<typeof ScreenshotWindow>;

const windowFits = (w: ScreenshotWindow, picture: { width: number; height: number }): boolean =>
  w.x + w.width <= picture.width && w.y + w.height <= picture.height;

/** What the browser declares at open about the screenshot it is about to upload (or couldn't take). */
export const DeclaredScreenshot = z.discriminatedUnion('state', [
  z
    .object({
      state: z.literal('pending_upload'),
      method: z.literal('page-redraw'),
      width: z.number().int().positive().max(100_000),
      height: z.number().int().positive().max(100_000),
      bytes: z.number().int().positive().max(5_242_880),
      sha256: z.string().regex(/^[0-9a-f]{64}$/),
      /** Where the visible window sits in the picture. Optional only for a tab from before QA-059. */
      window: ScreenshotWindow.optional(),
    })
    .strict()
    .refine((v) => v.window === undefined || windowFits(v.window, v), {
      message: 'The window must sit inside the picture.',
      path: ['window'],
    }),
  z.object({ state: z.literal('unavailable'), reason: CaptureReason }).strict(),
]);
export type DeclaredScreenshot = z.infer<typeof DeclaredScreenshot>;

export const OpenFeedback = z
  .object({
    /** Made in the browser, so a retried open is the same draft. */
    id: z.uuid(),
    page: FeedbackPage,
    client: FeedbackClient,
    screenshot: DeclaredScreenshot,
  })
  .strict();
export type OpenFeedback = z.infer<typeof OpenFeedback>;

/**
 * The draft is open. It has no FB number yet: the number is given at Send, so a discarded draft
 * never leaves a gap (`SubmittedFeedback.displayId`).
 */
export const OpenedFeedback = z
  .object({
    voice: z
      .object({
        available: z.boolean(),
        /** One-use ticket for the voice WebSocket; null when voice isn't available. */
        ticket: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type OpenedFeedback = z.infer<typeof OpenedFeedback>;

export const SubmitFeedback = z
  .object({
    words: z
      .string()
      .max(L.wordsChars)
      .refine((w) => w.trim() !== '', 'words_required'),
    kind: FeedbackKind.optional(),
    input: z.enum(['typed', 'spoken', 'both']),
    screenshot: z.enum(['include', 'exclude', 'unavailable']),
    /** The upload is still going; the report is sent now and settles within the attach window. */
    screenshotAttaching: z.boolean(),
    diagnostics: FeedbackDiagnostics,
  })
  .strict();
export type SubmitFeedback = z.infer<typeof SubmitFeedback>;

export const SubmittedFeedback = z
  .object({ displayId: z.string(), screenshot: ScreenshotStateDto })
  .strict();
export type SubmittedFeedback = z.infer<typeof SubmittedFeedback>;

/** Tells the server the screenshot upload ended without the file (or, empty, that it finished). */
export const CompleteScreenshot = z
  .object({
    omitted: z.enum(['upload_failed', 'upload_refused']).optional(),
    status: z.number().int().min(0).max(599).optional(),
  })
  .strict();
export type CompleteScreenshot = z.infer<typeof CompleteScreenshot>;

// ---- Review (§6) ----

const Person = z.object({ id: z.string(), name: z.string() });

export const FeedbackListQuery = z
  .object({
    /**
     * `open` is New, In review and Planned (the default view). `replies` is the reports with a
     * sender's reply the team hasn't read (APP-138 §D11).
     */
    status: z
      .union([TriageStatus, z.literal('open'), z.literal('all'), z.literal('replies')])
      .default('open'),
    kind: FeedbackKind.optional(),
    area: z.string().max(100).optional(),
    fromPersonId: z.uuid().optional(),
    sentFrom: z.iso.date().optional(),
    sentTo: z.iso.date().optional(),
    q: z.string().trim().max(200).optional(),
    ...pageFields,
  })
  .strict();
export type FeedbackListQuery = z.infer<typeof FeedbackListQuery>;

export const FeedbackListItem = z.object({
  id: z.uuid(),
  displayId: z.string(),
  sentAt: z.string(),
  from: Person,
  /** Role words at the time, and the persona when sent while previewing. */
  roleLine: z.string(),
  previewingAs: z.string().nullable(),
  title: z.string(),
  /** The title was written by the automated summary (shown with its label). */
  titleAutomated: z.boolean(),
  /** Null until someone sorts it: the reporter may leave it unchosen (APP-104 §3.3). */
  kind: FeedbackKind.nullable(),
  /** The kind was suggested by the automated summary, and no one has set it since. */
  kindAutomated: z.boolean(),
  status: TriageStatus,
  area: z.string(),
  pageTitle: z.string(),
  hasScreenshot: z.boolean(),
  spoken: z.boolean(),
  /** APP-138: the sender's replies the team hasn't read, and when the team last replied. */
  conversation: z
    .object({ toRead: z.number().int().min(0), lastTeamReplyAt: z.string().nullable() })
    .strict(),
});
export type FeedbackListItem = z.infer<typeof FeedbackListItem>;

export const FeedbackListPage = z.object({
  items: z.array(FeedbackListItem),
  page: PageInfo,
  /** The scoped stat chips: new now, and the last 30 days. */
  stats: z.object({
    new: z.number().int().min(0),
    last30Days: z.number().int().min(0),
    withScreenshotLast30Days: z.number().int().min(0),
    /** Whether any report has ever been sent, so "No feedback yet" is only for a truly empty inbox. */
    anySent: z.boolean(),
    /** APP-138: reports with a sender's reply the team hasn't read (the "Replies to read" tab). */
    repliesToRead: z.number().int().min(0),
    /** The sender replies themselves the team hasn't read (the hero's "Replies · to read"). */
    unreadReplies: z.number().int().min(0),
  }),
});
export type FeedbackListPage = z.infer<typeof FeedbackListPage>;

/**
 * The screenshot as a reviewer sees it: the report's state, except that a file a System Admin
 * deleted reads `deleted` and one the retention job purged reads `purged`. Those win over
 * `attached`, so a screenshot that's gone never reads as there.
 */
export const ReviewScreenshotState = z.enum([...ScreenshotStateDto.options, 'deleted', 'purged']);
export type ReviewScreenshotState = z.infer<typeof ReviewScreenshotState>;

const ScreenshotDetail = z.object({
  state: ReviewScreenshotState,
  /** Why it's missing, in the reviewer's words; null when attached or excluded. */
  reason: z.string().nullable(),
  scanning: z.boolean(),
  /**
   * Malware scanning's verdict on the stored screenshot; null when none is stored (never was, or
   * deleted or purged). `unscannable` means it couldn't be checked: the image is withheld, as
   * while `pending`. `malicious` means scanning found malware and removed it (the state is then
   * `omitted`).
   */
  scanStatus: z.enum(['pending', 'clean', 'not_required', 'unscannable', 'malicious']).nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  bytes: z.number().int().nullable(),
  /** The part of the picture the person had on screen; null when not recorded. */
  window: ScreenshotWindow.nullable(),
  purgedAt: z.string().nullable(),
  deleted: z.object({ by: Person, at: z.string(), reason: z.string() }).nullable(),
});

// ---- The conversation (APP-138 §D2, §D9) ----

const At = z.string();
const FeedbackInput = z.enum(['typed', 'spoken', 'both']);

/** The report itself, first in both timelines: the person's own words. */
const SentEntry = z
  .object({ type: z.literal('sent'), at: At, words: z.string(), input: FeedbackInput })
  .strict();

/**
 * One entry of the sender's conversation, in My feedback. A status entry carries the sender's
 * word and its date only, never who changed it, and the team's replies are the MyRewards team's,
 * never a person's. Contact lines aren't here at all. An answer a System Admin wrote in testing
 * (APP-071) says so, and never who wrote it, so it's never shown as the person's own.
 */
export const MyConversationEntry = z.discriminatedUnion('type', [
  SentEntry,
  z.object({ type: z.literal('status'), at: At, status: SenderStatus }).strict(),
  z
    .object({
      type: z.literal('team'),
      id: z.uuid(),
      at: At,
      body: z.string(),
      /** Written since the sender last opened this report. */
      new: z.boolean(),
    })
    .strict(),
  z
    .object({
      type: z.literal('you'),
      id: z.uuid(),
      at: At,
      body: z.string(),
      channel: FeedbackChannel,
      /** Written by a System Admin in testing (APP-071), not by the person. */
      simulated: z.boolean(),
    })
    .strict(),
]);
export type MyConversationEntry = z.infer<typeof MyConversationEntry>;

/**
 * One entry of the reviewers' timeline: the same conversation, with who wrote each team reply,
 * where it was delivered, who changed the status (triage words), and the contact lines only
 * reviewers see.
 */
export const ReviewConversationEntry = z.discriminatedUnion('type', [
  SentEntry,
  z
    .object({
      type: z.literal('status'),
      at: At,
      status: TriageStatus,
      /** Null when the audit entry has no person (a system rule). */
      by: Person.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('team'),
      id: z.uuid(),
      at: At,
      body: z.string(),
      author: Person,
      /** What the delivery job did; null until it has run. */
      delivered: z
        .object({ bell: z.boolean(), email: z.boolean(), teams: z.boolean() })
        .strict()
        .nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('sender'),
      id: z.uuid(),
      at: At,
      body: z.string(),
      channel: FeedbackChannel,
      /** Not yet read by the team when this page opened. */
      new: z.boolean(),
      /** The System Admin who wrote it in testing (APP-071); null for the person's own. */
      simulatedBy: Person.nullable(),
    })
    .strict(),
  z
    .object({
      type: z.literal('contact'),
      id: z.uuid(),
      at: At,
      via: z.enum(['outlook', 'teams']),
      by: Person,
      address: z.string(),
    })
    .strict(),
]);
export type ReviewConversationEntry = z.infer<typeof ReviewConversationEntry>;

/** The team's reply, with the status to set as it goes (APP-138 §D2). */
export const TeamReply = z
  .object({
    body: z.string().trim().min(1).max(L.teamReplyChars),
    status: TriageStatus.optional(),
    /** Optimistic concurrency, as in triage: a stale value is a 409 `feedback_changed`. */
    version: z.number().int().positive(),
  })
  .strict();
export type TeamReply = z.infer<typeof TeamReply>;

/** A reviewer pressed Email in Outlook or Chat in Teams; the report's history notes it (§D18). */
export const ContactPress = z
  .object({ via: z.enum(['outlook', 'teams']), address: z.email() })
  .strict();
export type ContactPress = z.infer<typeof ContactPress>;

export const FeedbackNote = z.object({
  id: z.uuid(),
  body: z.string(),
  by: Person,
  at: z.string(),
});
export type FeedbackNote = z.infer<typeof FeedbackNote>;

// Reports stored before recorded paths were checked can hold any path, so reading one must not
// fail on it: the detail's page and diagnostics take a plain string, and the web page only ever
// links a path after its own site-relative check (`safeInternalPath`).
const StoredPath = z.string().min(1).max(300);
/** Response side: the stored diagnostics, with paths as plain strings. */
export const StoredDiagnostics = z
  .object({
    requests: z.array(RecordedRequest.extend({ path: StoredPath })).max(L.ringRequests),
    errors: z.array(RecordedError).max(L.ringErrors),
    pages: z.array(VisitedPage.extend({ path: StoredPath })).max(L.ringPages),
  })
  .strict();

/** Response side: the stored page, with its path as a plain string. */
export const StoredPage = FeedbackPage.extend({ path: StoredPath });

export const FeedbackDetail = z.object({
  id: z.uuid(),
  displayId: z.string(),
  version: z.number().int().positive(),
  title: z.string(),
  titleAutomated: z.boolean(),
  kindAutomated: z.boolean(),
  /** The automated summary, only while it still matches the words it was made for. */
  summary: z.object({ text: z.string(), model: z.string() }).nullable(),
  words: z.string(),
  /**
   * What was said, session by session as heard (saved and interrupted ones), in order; null for a
   * typed report or once the evidence is purged.
   */
  spoken: z.array(SpokenSession).nullable(),
  input: z.enum(['typed', 'spoken', 'both']),
  sentAt: z.string(),
  from: Person.extend({ eeNumber: z.string().nullable() }),
  roleLine: z.string(),
  previewingAs: z.string().nullable(),
  page: StoredPage,
  client: FeedbackClient,
  appVersion: z.object({ tab: z.string(), server: z.string(), tabIsOlder: z.boolean() }),
  /** What the server captured at the click (APP-104 §4). */
  server: z
    .object({
      appVersion: z.string().max(80),
      revision: z.string().max(200).nullable(),
      replica: z.string().max(200).nullable(),
      environment: z.string().max(40),
    })
    .strict(),
  triage: z.object({
    status: TriageStatus,
    kind: FeedbackKind.nullable(),
    priority: FeedbackPriority.nullable(),
    duplicateOf: z.string().nullable(),
    lastChangedBy: Person.nullable(),
    lastChangedAt: z.string().nullable(),
  }),
  screenshot: ScreenshotDetail,
  /**
   * The viewer may delete the stored screenshot now: a System Admin, with one stored (APP-104
   * §5.4). Support reviews only, so the page hides Delete for them.
   */
  canDeleteScreenshot: z.boolean(),
  /** Null once the evidence is purged. */
  diagnostics: StoredDiagnostics.nullable(),
  notes: z.array(FeedbackNote),
  /**
   * Copy log query: a ready KQL query over ContainerAppConsoleLogs for the report's request IDs
   * and time window. Null when no request IDs were captured or diagnostics were purged first.
   */
  logQuery: z.string().max(8000).nullable(),
  /**
   * The server's own log lines for this report's requests (APP-104 §4). `entries` are text only,
   * oldest first, and are empty until the lines have been checked for malware. Null when the
   * report has no log to show (nothing was collected, or the evidence was purged).
   */
  serverLogs: z
    .object({
      state: z.enum(['pending', 'attached', 'incomplete', 'none', 'unchecked', 'unavailable']),
      lines: z.number().int().nonnegative(),
      /** `pending` only: true while the stored lines are being checked for malware. */
      scanning: z.boolean(),
      entries: z.array(z.object({ at: z.string(), text: z.string().max(2000) })).max(500),
    })
    .nullable(),
  /** APP-138: the conversation, oldest first. */
  conversation: z.array(ReviewConversationEntry),
  /** The sender's replies the team hadn't read when this page opened. */
  toRead: z.number().int().min(0),
  /**
   * Reaching the sender in Outlook or Teams (§D18), as finished links: Email in Outlook to the
   * work email, then the sign-in name (once when they're the same), and Chat in Teams by sign-in
   * name (null without one). The links open in the reviewer's own Outlook and Teams.
   */
  contact: z
    .object({
      workEmail: z.string().nullable(),
      upn: z.string().nullable(),
      email: z.array(
        z.object({ address: z.string(), kind: z.enum(['work', 'upn']), url: z.string() }).strict(),
      ),
      teams: z.string().nullable(),
    })
    .strict(),
});
export type FeedbackDetail = z.infer<typeof FeedbackDetail>;

export const UpdateTriage = z
  .object({
    /** Optimistic concurrency: a stale value is a 409 `feedback_changed`. */
    version: z.number().int().positive(),
    triageStatus: TriageStatus.optional(),
    kind: FeedbackKind.optional(),
    priority: FeedbackPriority.nullable().optional(),
    /** An FB reference such as FB-2026-0014. */
    duplicateOf: z
      .string()
      .trim()
      .regex(/^FB-\d{4}-\d{4,}$/)
      .nullable()
      .optional(),
  })
  .strict();
export type UpdateTriage = z.infer<typeof UpdateTriage>;

export const AddFeedbackNote = z
  .object({
    body: z
      .string()
      .max(L.noteChars)
      .refine((b) => b.trim() !== '', 'note_required'),
  })
  .strict();
export type AddFeedbackNote = z.infer<typeof AddFeedbackNote>;

export const DeleteScreenshot = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type DeleteScreenshot = z.infer<typeof DeleteScreenshot>;

// ---- My feedback: the sender's own reports (APP-138 §D1, §D8) ----

export const MyFeedbackListQuery = z
  .object({
    /** Open is Received, In review and Planned; closed is Done and Not planned. */
    view: z.enum(['open', 'closed', 'all']).default('open'),
    ...pageFields,
  })
  .strict();
export type MyFeedbackListQuery = z.infer<typeof MyFeedbackListQuery>;

export const MyFeedbackListItem = z
  .object({
    displayId: z.string(),
    title: z.string(),
    kind: FeedbackKind.nullable(),
    sentAt: At,
    status: SenderStatus,
    /** A duplicate, or a report others repeated: "Others reported this too". */
    othersToo: z.boolean(),
    /** The team's replies since the sender last opened it. */
    newReplies: z.number().int().min(0),
    /** The latest news: sent, a status change they can see, a team reply, or their own reply. */
    latest: z
      .object({
        kind: z.enum(['sent', 'status', 'team_reply', 'your_reply']),
        at: At,
        status: SenderStatus.optional(),
      })
      .strict(),
  })
  .strict();
export type MyFeedbackListItem = z.infer<typeof MyFeedbackListItem>;

export const MyFeedbackList = z
  .object({
    items: z.array(MyFeedbackListItem),
    page: PageInfo,
    /** The hero numbers: "Replies · new", "Your feedback · open", "Your feedback · ‹year›". */
    stats: z
      .object({
        newReplies: z.number().int().min(0),
        open: z.number().int().min(0),
        sentThisYear: z.number().int().min(0),
        year: z.number().int(),
        /** Whether they've ever sent any, so "Nothing sent yet" is only for a truly empty list. */
        anySent: z.boolean(),
        /** `feedback.evidenceDays`: the screenshot and page details go this many days after Send. */
        evidenceDays: z.number().int().min(1),
      })
      .strict(),
  })
  .strict();
export type MyFeedbackList = z.infer<typeof MyFeedbackList>;

export const MyFeedbackDetail = z
  .object({
    displayId: z.string(),
    title: z.string(),
    kind: FeedbackKind.nullable(),
    sentAt: At,
    /** The page they sent it from; null once the page details are gone. */
    page: z.object({ label: z.string(), path: StoredPath }).strict().nullable(),
    input: FeedbackInput,
    status: SenderStatus,
    othersToo: z.boolean(),
    /** The stepper: each step and the date it was reached (null when not reached). */
    steps: z.array(z.object({ status: SenderStatus, at: At.nullable() }).strict()),
    conversation: z.array(MyConversationEntry),
    /** Replying is open now (feedback is on, and this isn't Preview). */
    canReply: z.boolean(),
    screenshot: z.discriminatedUnion('state', [
      z.object({ state: z.literal('kept'), until: At }).strict(),
      z.object({ state: z.literal('removed'), on: At }).strict(),
      z.object({ state: z.literal('none') }).strict(),
    ]),
  })
  .strict();
export type MyFeedbackDetail = z.infer<typeof MyFeedbackDetail>;

/** The sender's reply in MyRewards; email and Teams answers keep the same cap. */
export const SenderReply = z.object({ body: z.string().trim().min(1).max(L.replyChars) }).strict();
export type SenderReply = z.infer<typeof SenderReply>;

// ---- Settings (§7) ----

/** APP-106's shape; mirrors the domain's AudiencePolicy. */
export const AudiencePolicy = z
  .object({
    everyone: z.boolean(),
    roles: z.array(RoleKey).max(RoleKey.options.length),
    personIds: z.array(z.uuid()).max(500),
  })
  .strict()
  .refine(
    (v) =>
      new Set(v.roles).size === v.roles.length && new Set(v.personIds).size === v.personIds.length,
    { message: 'duplicate_audience_entry' },
  );
export type AudiencePolicy = z.infer<typeof AudiencePolicy>;

export const FeedbackSettings = z
  .object({
    enabled: z.boolean(),
    audience: AudiencePolicy,
    evidenceDays: z.number().int().min(1).max(3650),
    /** Shows the pointer to the operating system's own dictation under the words box. */
    dictationTip: z.boolean(),
    /**
     * APP-138 §D18: where Email in Outlook and Chat in Teams open, as two web addresses (the
     * registry checks each is one of Microsoft 365's own for that app, an https origin alone:
     * SENT-026), or null to follow the cloud MyRewards runs in.
     */
    contactLinks: z
      .object({ outlook: z.string().max(300), teams: z.string().max(300) })
      .strict()
      .nullable(),
  })
  .strict();
export type FeedbackSettings = z.infer<typeof FeedbackSettings>;

/** The settings at once, with the one reason every change needs (APP-104 §7). */
export const UpdateFeedbackSettings = z
  .object({ ...FeedbackSettings.shape, reason: z.string().trim().min(1).max(2000) })
  .strict();
export type UpdateFeedbackSettings = z.infer<typeof UpdateFeedbackSettings>;

const LastChanged = z
  .object({ by: Person.nullable(), at: z.string(), reason: z.string().nullable() })
  .nullable();

/** The Feedback settings card: Support reads it, only a System Admin changes it. */
export const FeedbackSettingsView = z.object({
  settings: FeedbackSettings,
  canEdit: z.boolean(),
  /** "Last changed by … on …" for each setting; null while it's the built-in default. */
  lastChanged: z.object({
    enabled: LastChanged,
    audience: LastChanged,
    evidenceDays: LastChanged,
    dictationTip: LastChanged,
    contactLinks: LastChanged,
  }),
  /** The audience's named people with their names, in its order. */
  people: z.array(Person),
});
export type FeedbackSettingsView = z.infer<typeof FeedbackSettingsView>;
